// UC2 (well exception correlation) view models.
// These types are owned by the workflow-inbox feature and are deliberately kept
// separate from the current WorkflowDto mapping: the API still returns untyped
// EvidenceDataJson, so a later backend adapter can populate these without
// rewriting the UC2 components.

import type { Graph } from './graph';

export enum Uc2Subject {
  Exception = 'exception',
  Cascade = 'cascade',
}

/** Named lifecycle states from the UC2 spec. */
export enum Uc2State {
  Detected = 'Detected',
  Assigned = 'Assigned',
  UnderAnalystReview = 'UnderAnalystReview',
  AwaitingEngineerApproval = 'AwaitingEngineerApproval',
  AgreedResolutionRecorded = 'AgreedResolutionRecorded',
  Dispatch = 'Dispatch',
  Verify = 'Verify',
  End = 'End',
  Dismissed = 'Dismissed',
}

/** The generalized workflow engine's step classes that the states collapse onto. */
export enum Uc2Stage {
  Detection = 'Detection',
  AnalystDecision = 'AnalystDecision',
  EngineerDecision = 'EngineerDecision',
  DispatchVerify = 'DispatchVerify',
  Closed = 'Closed',
}

export enum Uc2Role {
  IoAnalyst = 'IO-Analyst',
  IoProductionEngineer = 'IO-Production-Engineer',
  IoAdmin = 'IO-Admin',
  IoFieldSpecialist = 'IO-Field-Specialist',
  IoUser = 'IO-User',
}

export enum Uc2FaultClass {
  LiftGasStarvation = 'LiftGasStarvation',
  InflowIssue = 'InflowIssue',
  OutflowIssue = 'OutflowIssue',
  Undetermined = 'Undetermined',
}

export enum Uc2PressureRole {
  CasingHead = 'casing-head',
  WellHead = 'well-head',
  Tubing = 'tubing',
  FlowLine = 'flow-line',
}

export enum Uc2Direction {
  Drop = 'drop',
  Rise = 'rise',
}

/** Binding types in spec priority order; most specific common cause wins. */
export enum Uc2BindingType {
  SharedGasLiftSupply = 'shared-gas-lift-supply',
  SharedFlowPath = 'shared-flow-path',
  SharedJacket = 'shared-jacket',
  SharedEquipment = 'shared-equipment',
}

/** Where a member landed against the two-band Signal to Case threshold. */
export enum Uc2Grouping {
  AutoAttached = 'auto-attached',
  Suggested = 'suggested',
}

export enum Uc2Destination {
  OffshoreOperations = 'Offshore operations',
  IocOffice = 'IOC office',
}

export enum Uc2Disposition {
  Recovered = 'recovered',
  NotRecovered = 'not_recovered',
  SelfRecovered = 'self_recovered',
}

export enum Uc2AuditType {
  Detected = 'Detected',
  Claimed = 'Claimed',
  AnalystConfirmed = 'AnalystConfirmed',
  MemberSplit = 'MemberSplit',
  Dismissed = 'Dismissed',
  EngineerApproved = 'EngineerApproved',
  EngineerReturned = 'EngineerReturned',
  FanDown = 'FanDown',
  DispatchRecorded = 'DispatchRecorded',
  Verified = 'Verified',
  RecoveryRecorded = 'RecoveryRecorded',
  Compensating = 'Compensating',
}

export interface Uc2SignalMetrics {
  tag: string;
  units: string;
  baselineMedian: number;
  modifiedZ: number;
  deviationPsi: number;
  deviationPercentOfBand: number;
  persistenceSamples: number;
  cusumOnsetUtc: string;
  breachedLimit: 'Low' | 'High' | null;
}

/**
 * Machine-resolvable citation. `graph` carries the visualization payload the spec
 * requires so the review surface can render the anomaly without a second fetch.
 */
export interface Uc2Citation {
  wellCode: string;
  pressureRole: Uc2PressureRole;
  direction: Uc2Direction;
  windowStartUtc: string;
  windowEndUtc: string;
  metrics: Uc2SignalMetrics;
  ontologyEdges: string[];
  prose: string;
  graph: Graph;
  bandLow: number | null;
  bandHigh: number | null;
  breachThreshold: number | null;
  onsetUtc: string;
  detectedUtc: string;
}

export interface Uc2Member {
  exceptionId: string;
  wellCode: string;
  wellTypeCode: 'OP' | 'GP';
  productionMethodCode: string | null;
  pressureRole: Uc2PressureRole;
  direction: Uc2Direction;
  onsetUtc: string;
  isRoot: boolean;
  grouping: Uc2Grouping;
  /** Bounded 0-1 contribution derived from settled signal metrics. */
  contribution: number;
  contributionBand: 'Strong' | 'Moderate' | 'Weak';
  dataQualityFlags: string[];
  citation: Uc2Citation;
}

export interface Uc2Hypothesis {
  id: string;
  rank: number;
  /** Null for a standalone exception, which has no cascade binding. */
  bindingType: Uc2BindingType | null;
  causeEntity: string;
  causeEntityType: string;
  /** binding-priority x coverage x cause-consistency. */
  compositeScore: number;
  confidenceBand: 'High' | 'Medium' | 'Low';
  coverage: number;
  explainedMemberIds: string[];
  consistencyRationale: string;
  topologyPath: string[];
  proposedResolution: string;
  proposedFaultClass: Uc2FaultClass;
  rootMemberId: string | null;
}

export interface Uc2AnalystGate {
  eventId: string;
  actor: string;
  actorRole: Uc2Role.IoAnalyst;
  timestampUtc: string;
  proposedFaultClass: Uc2FaultClass;
  assessedFaultClass: Uc2FaultClass;
  confirmedHypothesisId: string;
  causalClaimConfirmed: boolean;
  agreedResolution: string;
  reason: string | null;
}

export interface Uc2EngineerGate {
  actor: string;
  timestampUtc: string;
  approved: boolean;
  /** References the analyst gate event id rather than copying its values. */
  analystGateEventId: string;
}

/** Matches the Microsoft Graph user resource so a real directory lookup drops in unchanged. */
export interface Uc2Assignee {
  id: string;
  displayName: string;
  mail: string;
  jobTitle: string;
}

export interface Uc2DispatchAction {
  id: string;
  action: string;
  actionTaken?: string | null;
  destination: Uc2Destination;
  externalReference: string | null;
  observation: string | null;
  completedUtc: string | null;
}

export interface Uc2Verification {
  actor: string;
  timestampUtc: string;
  worked: boolean;
}

export interface Uc2Recovery {
  faultClass: Uc2FaultClass;
  causeConfirmation: 'confirmed' | 'rejected';
  actionsSummary: string[];
  disposition: Uc2Disposition;
  actor: string;
  timestampUtc: string;
  note: string | null;
}

export interface Uc2AuditEvent {
  id: string;
  type: Uc2AuditType;
  timestampUtc: string;
  actor: string;
  actorRole: Uc2Role | 'System';
  fromState: Uc2State | null;
  toState: Uc2State | null;
  summary: string;
  reason: string | null;
  memberId: string | null;
}

export interface Uc2Ownership {
  /** Null while the case sits unowned in a role queue. */
  owner: string | null;
  ownerRole: Uc2Role;
  queueRole: Uc2Role;
  priorAnalyst: string | null;
}

export interface Uc2Clocks {
  detectedUtc: string;
  reconstructedOnsetUtc: string;
  /** Floored by the 6h sweep granularity, per the spec's honesty requirement. */
  detectionLatencyHours: number;
  sweepQuantizationHours: number;
  /** Stops at AgreedResolutionRecorded even though the case runs on to closure. */
  responseStoppedUtc: string | null;
  responseTargetHours: number;
}

export interface Uc2Case {
  id: string;
  reference: string;
  title: string;
  subject: Uc2Subject;
  state: Uc2State;
  facility: string;
  ownership: Uc2Ownership;
  clocks: Uc2Clocks;
  members: Uc2Member[];
  hypotheses: Uc2Hypothesis[];
  analystGate: Uc2AnalystGate | null;
  engineerGate: Uc2EngineerGate | null;
  dispatchActions: Uc2DispatchAction[];
  verification: Uc2Verification | null;
  recovery: Uc2Recovery | null;
  selfRecoverySignalled: boolean;
  audit: Uc2AuditEvent[];
}
